import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Search, Eye, User, MapPin, Edit } from "lucide-react";
import { toast } from "sonner";
import { AdminJobDetailDialog } from "./AdminJobDetailDialog";

interface JobProfile {
  full_name: string;
  email?: string;
  company_name?: string;
}

interface Job {
  id: string;
  job_number: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  location: string;
  budget_min: number | null;
  budget_max: number | null;
  agreed_cost: number | null;
  required_date: string | null;
  required_specialties: string[] | null;
  job_category: string | null;
  created_at: string;
  updated_at: string;
  customer_profile: JobProfile | null;
  tradesman_profile: JobProfile | null;
}

export function AdminJobsManagement() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = jobs.filter(job => 
        job.job_number.toString().includes(searchTerm) ||
        job.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.id.slice(0, 8).toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.customer_profile?.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.tradesman_profile?.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.tradesman_profile?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredJobs(filtered);
    } else {
      setFilteredJobs(jobs);
    }
  }, [searchTerm, jobs]);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from("jobs")
        .select(`
          id,
          job_number,
          title,
          description,
          status,
          priority,
          location,
          budget_min,
          budget_max,
          agreed_cost,
          required_date,
          required_specialties,
          job_category,
          created_at,
          updated_at,
          customer_profile:customer_id (
            full_name,
            email,
            phone
          ),
          tradesman_profile:tradesman_id (
            full_name,
            company_name,
            email
          )
        `)
        .order("job_number", { ascending: false });

      if (error) throw error;
      setJobs((data as any) || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "pending":
        return "secondary";
      case "assigned":
        return "default";
      case "in_progress":
        return "outline";
      case "completed":
        return "default";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "text-yellow-600";
      case "assigned":
        return "text-blue-600";
      case "in_progress":
        return "text-orange-600";
      case "completed":
        return "text-green-600";
      case "cancelled":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "text-red-500";
      case "high":
        return "text-orange-500";
      case "standard":
        return "text-blue-500";
      case "low":
        return "text-gray-500";
      default:
        return "text-gray-500";
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "Not set";
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewJob = (job: Job) => {
    setSelectedJob(job);
    setShowJobDetail(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Job Management</CardTitle>
            <div className="text-sm text-muted-foreground">
              Last refreshed: {lastRefresh.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 mb-6">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by Job ID, job number, title, customer, or tradesman..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Tradesman</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground">
                      {searchTerm ? "No jobs found matching your search" : "No jobs found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredJobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="font-mono">
                        <div>
                          <div className="text-xs text-muted-foreground">ID: {job.id.slice(0, 8)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{job.title}</div>
                          <div className="text-sm text-muted-foreground flex items-center mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {job.location}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={getStatusBadgeVariant(job.status)}
                          className={getStatusColor(job.status)}
                        >
                          {job.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`capitalize ${getPriorityColor(job.priority)}`}>
                          {job.priority}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1 text-muted-foreground" />
                          <div>
                            <div className="font-medium text-sm">
                              {job.customer_profile?.full_name || "Unknown"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {job.customer_profile?.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {job.tradesman_profile ? (
                          <div>
                            <div className="font-medium text-sm">
                              {job.tradesman_profile.full_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {job.tradesman_profile.company_name}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {job.agreed_cost ? (
                            <span className="font-medium text-green-600">
                              {formatCurrency(job.agreed_cost)} (Agreed)
                            </span>
                          ) : (
                            <span>
                              {job.budget_min && job.budget_max 
                                ? `${formatCurrency(job.budget_min)} - ${formatCurrency(job.budget_max)}`
                                : job.budget_min 
                                ? `From ${formatCurrency(job.budget_min)}`
                                : job.budget_max
                                ? `Up to ${formatCurrency(job.budget_max)}`
                                : "Not set"
                              }
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(job.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewJob(job)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredJobs.length} of {jobs.length} jobs
          </div>
        </CardContent>
      </Card>

      <AdminJobDetailDialog
        job={selectedJob}
        open={showJobDetail}
        onOpenChange={setShowJobDetail}
        onJobUpdated={fetchJobs}
      />
    </div>
  );
}